import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useChatStore = create((set) => ({
  groups: [],
  totalGroups: 0,
  // refetchChatMembers: false,
  members: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },
  groupsLoading: false, // Add loading state for groups

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get drop down groups
  getDropdownGroups: async ({ successCallback }) => {
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_GROUPS_DROPDOWN
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
    }
  },

  // Function to get chat messages
  getChatMessages: async ({ id, successCallback }) => {
    useLoadingStore.getState().setChatMessagesLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_CHAT_MESSAGES,
        null,
        null,
        id
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setChatMessagesLoading(false);
    }
  },

  // Function to close chat
  closeChat: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.CLOSE_CHAT,
        { isClosed: true },
        null,
        id
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get all chat
  getAllChat: async ({ query = null, successCallback }) => {
    useLoadingStore.getState().setChatListLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_ALL_CHAT,
        null,
        query
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setChatListLoading(false);
    }
  },

  // Function to get chat members
  getChatMembers: async ({ query = null, successCallback }) => {
    useLoadingStore.getState().setChatMembersLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_CHAT_MEMBERS,
        null,
        query
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setChatMembersLoading(false);
    }
  },

  // Function to get chat members
  getChatAdmins: async ({ query = null, successCallback }) => {
    useLoadingStore.getState().setChatMembersLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_CHAT_ADMINS,
        null,
        query
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setChatMembersLoading(false);
    }
  },

  // Function to delete chat
  deleteChat: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_CHAT,
        null,
        null,
        id
      );
      if (response.success) {
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get members
  getMembers: async () => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_CHAT_ADMINS
      );
      if (response.success) {
        set((state) => ({
          members: response.data?.data || [],
        }));
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get groups
  getGroups: async ({ query = null, successCallback }) => {
    set({ groupsLoading: true });
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_GROUP,
        null,
        query
      );

      if (response.success) {
        set((state) => ({
          groups: response.data?.data || [],
          totalGroups: response.data?.pagination?.totalGroupCount || 0,
          pagination: {
            ...state.pagination,
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        }));
        successCallback && successCallback();
      }
    } catch (error) {
      // Handle error
    } finally {
      set({ groupsLoading: false }); // Set loading state to false
    }
  },

  // Function to create the group
  createGroup: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_GROUP,
        data
      );

      if (response.success) {
        successCallback && successCallback(response);
        await useChatStore.getState().getGroups({
          successCallback: () => {
            useChatStore.getState().resetCurrentPage();
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete group
  deleteGroup: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_GROUP,
        null,
        null,
        id
      );

      if (response.success) {
        successCallback && successCallback(response);
        await useChatStore.getState().getGroups({
          successCallback: () => {
            useChatStore.getState().resetCurrentPage();
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update group
  updateGroup: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_GROUP,
        data,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedGroup = state.groups.find((group) => group.id === id);
          if (updatedGroup) {
            const updatedGroups = [
              { ...updatedGroup, ...response.data },
              ...state.groups.filter((group) => group.id !== id),
            ];
            return {
              groups: updatedGroups,
              pagination: {
                ...state.pagination,
                currentPage: 1,
              },
            };
          }
          return state;
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to active/inactive group
  toggleGroupStatus: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.CHANGE_GROUP_STATUS,
        data,
        null,
        id
      );
      if (response.success) {
        set((state) => ({
          groups: state.groups.map((group) =>
            group.id === id ? { ...group, ...data } : group
          ),
        }));
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useChatStore;
